import classnames from 'classnames/bind';
import { Bar } from 'react-chartjs-2';

import { DataWidget } from 'types/dashboard_widget';

import { WIDGET_REFETCH_INTERVAL } from 'dashboards/services/interval';
import { createCsvFile } from 'services/csv';
import { downloadFile } from 'services/download_file';
import { getDimensionsFromMetricsResponse } from 'dashboards/services/metric';
import { hslaToString } from 'dashboards/services/color';
import { rowsToTimezone } from 'dashboards/services/table';
import { sanitizeDataWidgetFilters } from 'dashboards/components/DataWidgetFiltersInput/sanitize';
import { useChartColors } from 'dashboards/hooks/useChartColors';
import { useLeaderboard } from 'dashboards/hooks/useLeaderboard';
import { useTimeRangeParams } from 'dashboards/hooks/useTimeRangeParams';
import { useTopLevelDataWidgetFilters } from 'dashboards/hooks/useTopLevelDataWidgetFilters';
import { useUserMetrics } from 'dashboards/hooks/useUserMetrics';

import { ChartError } from 'dashboards/components/ChartError/ChartError';
import { ChartLoader } from 'dashboards/components/ChartLoader/ChartLoader';
import { Icon } from 'components/Icon/IconDeprecated';
import { Widget } from 'dashboards/widgets/Widget/Widget';

import styles from './Chart.module.scss';

const c = classnames.bind(styles);

type LeaderboardProps = {
  widget: DataWidget<'leaderboard-bar'>;
};

/** Component for {@link ChartType} leaderboard-bar */
export function Leaderboard({ widget }: LeaderboardProps) {
  const { metric, ...query } = widget.query;

  const { start, stop, timezone } = useTimeRangeParams();

  const { data: userMetrics } = useUserMetrics();

  const columns = getDimensionsFromMetricsResponse(metric, userMetrics);

  const filters = useTopLevelDataWidgetFilters({
    filters: sanitizeDataWidgetFilters(widget.query.filters),
    columns,
  });

  const {
    data,
    isLoading: isLoadingLeaderboard,
    error,
  } = useLeaderboard({
    params: {
      metricName: metric,
      timeRange: { start, stop },
      timeZone: timezone,
      ...query,
      filters,
    },
    refetchInterval: WIDGET_REFETCH_INTERVAL,
    keepPreviousData: true,
  });

  // Use first label to determine bar color
  const colorLabels = data?.rows.map(([label]) => label);

  const { data: colors, isLoading: isLoadingColors } =
    useChartColors(colorLabels);

  const isLoading = isLoadingLeaderboard || isLoadingColors;

  if (error) {
    return (
      <Widget widget={widget}>
        <ChartError />
      </Widget>
    );
  }

  if (!data && isLoading) {
    return (
      <Widget widget={widget}>
        <ChartLoader />
      </Widget>
    );
  }

  if (!data || !colors) {
    return null;
  }

  const { headers, rows } = data;

  // Index of the value column (always the last column)
  const valueIndex = headers.length - 1;

  const labels = rows.map((row) => row.slice(0, valueIndex));
  const values = rows.map((row) => parseInt(row[valueIndex], 10));

  const datasets = [
    {
      label: metric,
      data: values,
      backgroundColor: colors.map(hslaToString),
      borderColor: colors.map(hslaToString),
    },
  ];

  return (
    <Widget
      widget={widget}
      menuItems={{
        csv: {
          children: 'Export as CSV',
          leftSection: <Icon name="export" size="small" />,
          onClick: () =>
            downloadFile(
              widget.name,
              createCsvFile(rowsToTimezone({ headers, rows, timezone }))
            ),
          disabled: rows.length === 0,
        },
      }}
    >
      <div className={c('chart')}>
        <Bar
          options={{
            responsive: true,
            maintainAspectRatio: false,
            indexAxis: 'y',
            plugins: {
              legend: {
                display: false,
              },
            },
          }}
          data={{ labels, datasets }}
        />
      </div>
    </Widget>
  );
}
